import { useEffect, useRef, useState } from "react";
import "./BootSplash.css";

const MIN_DURATION_MS = 3_000;
const MAX_DURATION_MS = 7_000;

function randomDurationMs(): number {
  const range = MAX_DURATION_MS - MIN_DURATION_MS + 1;

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const value = new Uint32Array(1);
    crypto.getRandomValues(value);
    return MIN_DURATION_MS + (value[0] % range);
  }

  return MIN_DURATION_MS + Math.floor(Math.random() * range);
}

export interface BootSplashProps {
  onComplete?: () => void;
}

export function BootSplash({ onComplete }: BootSplashProps) {
  const [progress, setProgress] = useState(0);
  const durationMs = useRef(randomDurationMs());
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const start = performance.now();
    let animationFrame = 0;
    let completed = false;

    const advance = (now: number) => {
      const nextProgress = Math.min(100, ((now - start) / durationMs.current) * 100);
      setProgress(nextProgress);

      if (nextProgress < 100) {
        animationFrame = requestAnimationFrame(advance);
        return;
      }

      if (!completed) {
        completed = true;
        onCompleteRef.current?.();
      }
    };

    animationFrame = requestAnimationFrame(advance);

    return () => {
      completed = true;
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const roundedProgress = Math.round(progress);

  return (
    <div className="boot-splash" role="status" aria-live="polite" aria-busy="true">
      <div className="boot-splash__ambient" aria-hidden="true" />
      <div className="boot-splash__content">
        <div className="boot-splash__spinner" aria-hidden="true">
          <span />
        </div>
        <p className="boot-splash__brand">Roleka By Webrain</p>
        <p className="boot-splash__message">読み込み中…</p>
        <div
          className="boot-splash__progress"
          role="progressbar"
          aria-label="起動準備の進捗"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={roundedProgress}
        >
          <span
            className="boot-splash__progress-fill"
            style={{ transform: `scaleX(${progress / 100})` }}
          />
        </div>
        <span className="boot-splash__progress-value" aria-hidden="true">
          {roundedProgress}%
        </span>
      </div>
    </div>
  );
}
